import type {
    SelectFilterOption,
    Make,
    Model,
    EngineSize,
    Transmission,
    FuelType,
    Year,
    FilteredOptionsResponse,
} from '../types/manualsearch.types';

/**
 * Transforms makes array into SelectFilterOption format
 */
function transformMakes(makes: Make[] | undefined): SelectFilterOption[] {
    if (!makes || !Array.isArray(makes)) {
        return [];
    }

    return makes
        .filter((make) => make && make.id && make.name) // Filter out invalid entries
        .map((make) => ({
            value: make.id.toString(),
            label: make.name,
        }))
        .filter(
            (option, index, self) =>
                // Remove duplicates based on value
                self.findIndex((o) => o.value === option.value) === index
        );
}

/**
 * Transforms models array into SelectFilterOption format
 */
function transformModels(models: Model[] | undefined): SelectFilterOption[] {
    if (!models || !Array.isArray(models)) {
        return [];
    }

    return models
        .filter((model) => model && model.id && model.name)
        .map((model) => ({
            value: model.id.toString(),
            label: model.name,
        }))
        .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index);
}

/**
 * Transforms engine sizes array into SelectFilterOption format
 */
function transformEngineSizes(engines: EngineSize[] | undefined): SelectFilterOption[] {
    if (!engines || !Array.isArray(engines)) {
        return [];
    }

    return engines
        .filter(
            (engine): engine is EngineSize & { description: string } =>
                Boolean(engine && engine.id && engine.description)
        )
        .map((engine) => ({
            value: engine.id.toString(),
            label: engine.description,
        }))
        .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index);
}

/**
 * Transforms transmissions array into SelectFilterOption format
 */
function transformTransmissions(transmissions: Transmission[] | undefined): SelectFilterOption[] {
    if (!transmissions || !Array.isArray(transmissions)) {
        return [];
    }

    return transmissions
        .filter((transmission) => transmission && transmission.id && transmission.type)
        .map((transmission) => ({
            value: transmission.id.toString(),
            label: transmission.type,
        }))
        .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index);
}

/**
 * Transforms fuel types array into SelectFilterOption format
 */
function transformFuelTypes(fuels: FuelType[] | undefined): SelectFilterOption[] {
    if (!fuels || !Array.isArray(fuels)) {
        return [];
    }

    return fuels
        .filter((fuel) => fuel && fuel.id && fuel.name)
        .map((fuel) => ({
            value: fuel.id.toString(),
            label: fuel.name,
        }))
        .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index);
}

/**
 * Transforms years array into SelectFilterOption format
 */
function transformYears(years: Year[] | undefined): SelectFilterOption[] {
    if (!years || !Array.isArray(years)) {
        return [];
    }

    return years
        .filter((year) => year && year.year && year.label)
        .map((year) => ({
            value: year.year,
            label: year.label,
        }))
        .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index);
}

/**
 * Transforms API response options into SelectFilterOption format for all dropdown types
 * Includes validation, deduplication, and proper type handling
 */
export function transformFilteredOptions(
    availableOptions: FilteredOptionsResponse['available_options']
): {
    makes: SelectFilterOption[];
    models: SelectFilterOption[];
    engine_sizes: SelectFilterOption[];
    transmissions: SelectFilterOption[];
    fuel_types: SelectFilterOption[];
    years: SelectFilterOption[];
} {
    // Add debug logging in development
    if (process.env.NODE_ENV === 'development') {
        const duplicateIds = (availableOptions.makes || [])
            .map((make) => make?.id)
            .filter((id, index, arr) => arr.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
            console.warn('Duplicate make IDs found:', duplicateIds);
        }
    }

    return {
        makes: transformMakes(availableOptions.makes),
        models: transformModels(availableOptions.models),
        engine_sizes: transformEngineSizes(availableOptions.engine_sizes),
        transmissions: transformTransmissions(availableOptions.transmissions),
        fuel_types: transformFuelTypes(availableOptions.fuel_types),
        years: transformYears(availableOptions.years),
    };
}

/**
 * Individual transformers for specific use cases
 */
export const transformers = {
    makes: transformMakes,
    models: transformModels,
    engineSizes: transformEngineSizes,
    transmissions: transformTransmissions,
    fuelTypes: transformFuelTypes,
    years: transformYears,
} as const;
